import type {
  Cart as PolarCart,
  CartLine as PolarCartLine,
  ProductVariant as PolarProductVariant,
} from '@polar-analytics/pixel-sdk';
import type { CartUpdateItem } from 'lib/analytics/schema';
import type { Cart } from 'types/cart/Cart';
import type { CartLineItem } from 'types/cart/CartLineItem';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import type { Variant } from 'types/product/Variant';
import { getProductVariant } from 'utils/product/getProductVariant';

import { VENDOR } from '../constants';

export const productVariantPayload = (
  product: ShopifyProduct,
  variant: Variant,
  url: string | null = null,
): PolarProductVariant => ({
  id: variant.id,
  sku: variant.sku,
  title: variant.title,
  untranslatedTitle: variant.title,
  image: variant.imageUrl ? { src: variant.imageUrl } : null,
  price: variant.price,
  product: {
    id: product.id,
    title: product.title,
    untranslatedTitle: product.title,
    type: product.productType,
    url,
    vendor: VENDOR,
  },
});

export const cartLinePayloadFromItem = ({
  product,
  merchandiseId,
  quantity,
}: CartUpdateItem): PolarCartLine | undefined => {
  const variant = getProductVariant(product, merchandiseId);

  if (!variant) return;

  return {
    quantity,
    cost: {
      totalAmount: {
        amount: variant.price.amount * quantity,
        currencyCode: variant.price.currencyCode,
      },
    },
    merchandise: productVariantPayload(product, variant),
  };
};

const cartLinePayloadFromLineItem = ({
  merchandise,
  cost,
  quantity,
}: CartLineItem): PolarCartLine => ({
  quantity,
  cost: { totalAmount: cost.totalAmount },
  merchandise: {
    id: merchandise.id,
    sku: merchandise.sku,
    title: merchandise.title,
    untranslatedTitle: merchandise.title,
    image: merchandise.image ? { src: merchandise.image.url } : null,
    price: merchandise.price,
    product: {
      id: merchandise.product.id,
      title: merchandise.product.title,
      untranslatedTitle: merchandise.product.title,
      type: null,
      url: null,
      vendor: VENDOR,
    },
  },
});

export const cartPayload = (cart: Cart): PolarCart => ({
  id: cart.id,
  attributes: cart.attributes,
  cost: { totalAmount: cart.cost.totalAmount },
  lines: cart.lines.map(cartLinePayloadFromLineItem),
  totalQuantity: cart.totalQuantity,
});
